import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { fmtTime } from "./GuestCard";
import { TagChips } from "./TagChips";
import { CircleUser, Clock, Users, MessageSquareText, Utensils, Wine, StickyNote, BadgeEuro, Printer, Phone, Mail, MessageCircle } from "lucide-react";
import { printReceipt } from "./receipt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATION_META = {
  kitchen: { label: "Food", Icon: Utensils, color: "text-amber-600" },
  bar: { label: "Drinks", Icon: Wine, color: "text-sea" },
};
const STATUS_META = {
  new: { label: "Received", cls: "bg-amber-400 text-amber-950" },
  preparing: { label: "Preparing", cls: "bg-sea text-white" },
  ready: { label: "Ready", cls: "bg-emerald-500 text-white" },
  served: { label: "Served", cls: "bg-sage/20 text-sage" },
};

const OrderRow = ({ o }) => {
  const sm = STATION_META[o.station] || STATION_META.bar;
  const st = STATUS_META[o.status] || STATUS_META.new;
  return (
    <div data-testid={`guest-order-${o.id}`} className="rounded-xl border border-sand-200 bg-white p-2.5">
      <div className="mb-1 flex items-center justify-between">
        <span className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wider ${sm.color}`}><sm.Icon size={13} /> {sm.label}</span>
        <div className="flex items-center gap-1.5">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${st.cls}`}>{st.label}</span>
          {o.invoiced && <span className="rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-brand-700">POS</span>}
        </div>
      </div>
      <ul className="space-y-0.5 text-sm">
        {o.items.map((it, i) => (
          <li key={`${it.name}-${i}`} className="flex justify-between"><span className="text-slate-700">{it.qty}× {it.name}</span><span className="text-slate-400">{(it.price * it.qty).toFixed(2)}€</span></li>
        ))}
      </ul>
      <div className="mt-1 flex justify-between text-xs font-semibold text-slate-500">
        <span>{o.source === "waiter" ? "via waiter" : "via QR"}</span><span>{o.total.toFixed(2)}€</span>
      </div>
    </div>
  );
};

export const GuestDetailDialog = ({ open, onOpenChange, detail, orders = [], onDone }) => {
  const [local, setLocal] = useState(orders);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setLocal(orders); }, [orders]);
  if (!detail) return null;
  const { lounge, occupant, reservation, note } = detail;
  const num = lounge.num != null ? lounge.num : (lounge.label || "").replace(/\D+/g, "");
  const code = (lounge.type === "umbrella" ? "U" : lounge.type === "villa" ? "V" : "G") + num;
  const title = occupant?.guest_name || lounge.label;
  const tags = reservation?.tags || [];
  const pax = reservation?.party_size;
  const outstanding = local.filter((o) => o.status !== "served");
  const done = local.filter((o) => o.status === "served");
  const spent = local.reduce((s, o) => s + o.total, 0);
  const unbilled = local.filter((o) => !o.invoiced);
  const unbilledTotal = unbilled.reduce((s, o) => s + o.total, 0);

  const sendAllToPos = async () => {
    setBusy(true);
    try {
      await Promise.all(unbilled.map((o) => axios.patch(`${API}/orders/${o.id}/flags`, { invoiced: true })));
      setLocal((prev) => prev.map((o) => ({ ...o, invoiced: true })));
      toast.success(`${unbilled.length} order(s) sent to POS · ${unbilledTotal.toFixed(2)}€`);
      onDone && onDone();
    } catch { toast.error("Could not send to POS"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="guest-detail-dialog" className="max-h-[85vh] gap-0 overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl tracking-wide">
            <CircleUser size={20} className="text-brand-700" /> {title}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
          <span className="rounded-full bg-slate-900 px-2 py-0.5 text-xs font-bold text-white">{code}</span>
          {pax > 0 && <span className="flex items-center gap-1"><Users size={13} /> {pax} pax</span>}
          {occupant && <span className="flex items-center gap-1"><Clock size={13} /> {fmtTime(occupant.start_date)}–{fmtTime(occupant.end_date)}</span>}
        </div>
        {reservation && (reservation.phone || reservation.email || reservation.guest_name) && (
          <div className="mt-3 space-y-1.5 rounded-xl bg-sand-50 p-3 ring-1 ring-sand-200" data-testid="guest-contact">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Guest contact</p>
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-800" data-testid="guest-fullname">
              <CircleUser size={14} className="shrink-0 text-slate-400" /> {reservation.guest_name}
            </p>
            {reservation.phone && (
              <a data-testid="guest-phone" href={`tel:${reservation.phone}`}
                className="flex items-center gap-2 text-sm font-medium text-brand-700 hover:underline">
                <Phone size={14} className="shrink-0 text-slate-400" /> {reservation.phone}
              </a>
            )}
            {reservation.email && (
              <a data-testid="guest-email" href={`mailto:${reservation.email}`}
                className="flex items-center gap-2 break-all text-sm font-medium text-brand-700 hover:underline">
                <Mail size={14} className="shrink-0 text-slate-400" /> {reservation.email}
              </a>
            )}
            {reservation.phone && (
              <a data-testid="guest-whatsapp"
                href={`https://wa.me/${reservation.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hello ${reservation.guest_name}, this is TRIBE Beach Lounge in Tigaki about your sunbed reservation. `)}`}
                target="_blank" rel="noreferrer"
                className="flex items-center gap-2 text-sm font-semibold text-emerald-600 hover:underline">
                <MessageCircle size={14} className="shrink-0" /> WhatsApp guest
              </a>
            )}
          </div>
        )}
        {tags.length > 0 && <div className="mt-2"><TagChips tags={tags} /></div>}
        {note && <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800"><StickyNote size={13} className="mt-px shrink-0" /> {note}</p>}
        {reservation?.special_requests && (
          <p className="mt-2 flex items-start gap-1.5 text-xs italic text-slate-500"><MessageSquareText size={13} className="mt-px shrink-0 text-slate-400" /> {reservation.special_requests}</p>
        )}
        {reservation?.comment && reservation.comment !== reservation.special_requests && (
          <p className="mt-1 flex items-start gap-1.5 text-xs italic text-slate-500" data-testid="guest-comment"><MessageSquareText size={13} className="mt-px shrink-0 text-slate-400" /> {reservation.comment}</p>
        )}

        <div className="mt-4 space-y-3">
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-brand-700">Outstanding · {outstanding.length}</p>
            {outstanding.length === 0 ? <p className="rounded-lg bg-sand-100 py-3 text-center text-sm text-slate-400">Nothing pending</p> : (
              <div className="space-y-2" data-testid="guest-outstanding">{outstanding.map((o) => <OrderRow key={o.id} o={o} />)}</div>
            )}
          </div>
          {done.length > 0 && (
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Fulfilled · {done.length}</p>
              <div className="space-y-2 opacity-75" data-testid="guest-fulfilled">{done.map((o) => <OrderRow key={o.id} o={o} />)}</div>
            </div>
          )}
          {local.length > 0 && (
            <div className="flex justify-between border-t border-sand-300 pt-2 text-sm font-bold text-slate-800">
              <span>Total ordered today</span><span data-testid="guest-total">{spent.toFixed(2)}€</span>
            </div>
          )}
          {unbilledTotal > 0 && (
            <div className="flex items-center justify-between rounded-xl bg-destructive/10 px-3 py-2 text-sm font-bold text-destructive" data-testid="guest-unpaid">
              <span className="flex items-center gap-1.5"><BadgeEuro size={15} /> Unpaid ({unbilled.length})</span>
              <span data-testid="guest-unpaid-total">{unbilledTotal.toFixed(2)}€</span>
            </div>
          )}
          {local.length > 0 && (
            <button data-testid="print-tab" onClick={() => printReceipt(title, local)}
              className="flex h-10 w-full items-center justify-center gap-2 rounded-full border border-sand-300 bg-white text-sm font-semibold text-slate-700 hover:bg-sand-100">
              <Printer size={15} /> Print tab
            </button>
          )}
          {unbilled.length > 0 && (
            <button data-testid="send-all-pos" onClick={sendAllToPos} disabled={busy}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-brand text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60">
              <BadgeEuro size={16} /> Send all to POS · {unbilledTotal.toFixed(2)}€
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
